import { toast } from 'sonner'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { handleError } from '@/services/errorHandler.js'
import { useParams } from 'react-router-dom'
import { TIKTOK_APP_ID } from '@/config-global.jsx'
import { useCurrentSpace } from '@/hooks/useCurrentSpace.jsx'
import { useAuthContext } from '@/context-state/AuthContext.jsx'
import { useSyncEntitlements } from '@/hooks/useSyncEntitlements'
import { useRelevantIntegration } from '@/hooks/useRelevantIntegration.jsx'
import { IntegrationProviders } from '@/consts/index.js'
import * as API from '@/services/api.js'

export const useTikTokConnection = () => {
    const { accountId } = useParams()
    const { updateSpaceState } = useAuthContext()
    const { space } = useCurrentSpace()
    const syncEntitlements = useSyncEntitlements()
    const { integration: tiktokIntegration } = useRelevantIntegration(IntegrationProviders.TIKTOK_ADS)
    const connectToTikTok = async () => {
        try {
            toggleLoader(true)

            const state = accountId
            const redirectUri = encodeURIComponent(`${window.location.origin}/connect/tiktok`)

            const tiktokAuthUrl = `https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}&state=${state}&redirect_uri=${redirectUri}`

            window.location.replace(tiktokAuthUrl)
        } catch (e) {
            console.error(e)
            toast.error(e.message)
            handleError(e)
            toggleLoader(false)
        }
    }

    const handleTikTokAdAccountSelection = async (selectedAccountIds) => {
        try {
            toggleLoader(true)

            if (tiktokIntegration?.adAccounts) {
                const selectedAdAccounts = tiktokIntegration.adAccounts
                    .filter(account => selectedAccountIds.includes(account.id))
                    .map(account => ({
                        ...account,
                        provider: tiktokIntegration?.provider || 'tiktok_ads'
                    }))

                const normalizeProvider = (provider) => String(provider || '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_+|_+$/g, '')

                const existingNonTikTokAccounts = space?.adAccounts?.filter(account => normalizeProvider(account.provider) !== 'tiktok_ads') || []
                const nextAdAccounts = [...existingNonTikTokAccounts, ...selectedAdAccounts]
                await API.updateSpace(space._id, { adAccounts: nextAdAccounts })

                const updatedSpace = { ...space, adAccounts: nextAdAccounts }
                updateSpaceState(space._id, updatedSpace)

                syncEntitlements()
                toast.success('Ad Accounts updated successfully')
            }
        } catch (e) {
            console.error(e)
            toast.error('An error occurred while updating ad accounts')
        } finally {
            toggleLoader(false)
        }
    }

    return { connectToTikTok, handleTikTokAdAccountSelection }
}
